/**
 * SUTRA BY TULIKA — LUXURY STOREFRONT INTERACTION SCRIPTS
 * Handles wishlist persistence, smooth scroll states, drawer controls, and micro-interactions
 */

(function () {
  'use strict';

  // 1. Wishlist Persistence & State Synchronization
  const WISHLIST_KEY = 'sutra_wishlist';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function setWishlist(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('Unable to save wishlist to localStorage', e);
    }
  }

  function syncWishlistButtons() {
    const saved = getWishlist();
    document.querySelectorAll('.sutra-wishlist-btn').forEach((btn) => {
      const pid = btn.getAttribute('data-product-id');
      if (pid && saved.includes(pid)) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  // Handle wishlist button clicks with smooth heartbeat feedback
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.sutra-wishlist-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const pid = btn.getAttribute('data-product-id');
    if (!pid) return;

    const saved = getWishlist();
    const index = saved.indexOf(pid);

    if (index > -1) {
      saved.splice(index, 1);
      btn.classList.remove('is-active');
      btn.setAttribute('aria-pressed', 'false');
    } else {
      saved.push(pid);
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('sutra-pulse-anim');
      setTimeout(() => btn.classList.remove('sutra-pulse-anim'), 600);
    }

    setWishlist(saved);

    // Sync other buttons for the same product on page
    document.querySelectorAll(`.sutra-wishlist-btn[data-product-id="${pid}"]`).forEach((otherBtn) => {
      if (otherBtn !== btn) {
        if (saved.includes(pid)) {
          otherBtn.classList.add('is-active');
          otherBtn.setAttribute('aria-pressed', 'true');
        } else {
          otherBtn.classList.remove('is-active');
          otherBtn.setAttribute('aria-pressed', 'false');
        }
      }
    });
  });

  // 2. Smooth Header Glassmorphism on Scroll
  function initHeaderScroll() {
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper) return;

    let ticking = false;

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > 30) {
            headerWrapper.classList.add('sutra-header--scrolled');
          } else {
            headerWrapper.classList.remove('sutra-header--scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // 3. Smooth Category & Product Card Reveal Effects
  function initCardEffects() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('sutra-in-view');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -30px 0px', threshold: 0.05 }
      );

      document
        .querySelectorAll(
          '.sutra-category-card, .sutra-product-card, .sutra-benefit-item, .sutra-insta-item, .sutra-about-anim, .sutra-about-card, .sutra-about-interlude, .sutra-about-lead-box'
        )
        .forEach((el) => {
          observer.observe(el);
        });
    }
  }

  // 4. Smooth Menu Drawer Click Outside, Escape Key & State Sync
  function initDrawerHelpers() {
    const details = document.getElementById('Details-menu-drawer-container');
    const headerWrapper = document.querySelector('.header-wrapper');
    const headerSection = document.querySelector('.section-header');

    if (details) {
      details.addEventListener('toggle', function () {
        if (details.open) {
          headerWrapper?.classList.add('menu-open');
          headerSection?.classList.add('menu-open');
        } else {
          headerWrapper?.classList.remove('menu-open');
          headerSection?.classList.remove('menu-open');
          document.body.classList.remove('overflow-hidden-desktop', 'overflow-hidden-tablet');
        }
      });
    }

    document.addEventListener('click', function (e) {
      if (!details || !details.hasAttribute('open')) return;

      const drawer = document.getElementById('menu-drawer');
      const summary = details.querySelector('summary');

      // If clicked outside of the drawer panel and outside of the summary toggle
      if (drawer && !drawer.contains(e.target) && !summary.contains(e.target)) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        document.body.classList.remove('overflow-hidden-desktop', 'overflow-hidden-tablet');
        headerSection?.classList.remove('menu-open');
        headerWrapper?.classList.remove('menu-open');
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (details && details.hasAttribute('open')) {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
          document.body.classList.remove('overflow-hidden-desktop', 'overflow-hidden-tablet');
          headerSection?.classList.remove('menu-open');
          headerWrapper?.classList.remove('menu-open');
        }
      }
    });
  }

  window.sutraInitAbout = initCardEffects;

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncWishlistButtons();
      initHeaderScroll();
      initCardEffects();
      initDrawerHelpers();
    });
  } else {
    syncWishlistButtons();
    initHeaderScroll();
    initCardEffects();
    initDrawerHelpers();
  }
  // Listen for Shopify theme editor section updates
  document.addEventListener('shopify:section:load', () => {
    syncWishlistButtons();
    initCardEffects();
  });
})();
